import { useLazyQuery, useMutation } from "@apollo/client/react"; import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CustomButton } from '../../../components/customButton/CustomButton';
import { CustomDialog } from '../../../components/customDialog/CustomDialog';
import { FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';

import { adaptUsuariosResponsablesCompletoArray, GET_ALL_USUARIOS_RESPONSABLES_COMPLETO } from '../../../data/graphQL/queries/Reclamos/getAllUsuariosResponsablesCompleto';
import { GET_USUARIOS_RESPONSABLES_SIN_VALIDAR } from '../../../data/graphQL/queries/Reclamos/getUsuariosResponsablesSinValidar';
import toast from 'react-hot-toast';
import AGREGAR_USUARIOS_RESPONSABLE from '../../../data/graphQL/mutations/Reclamo/AgregarUsuariosResponsable';
import ELIMINAR_USUARIO_RESPONSABLE from '../../../data/graphQL/mutations/Reclamo/EliminarUsuarioResponsable';

const STEP_RESPONSABLES_LISTA = 0;
const STEP_NUEVOS_RESPONSABLES = 1;

const EditarResponsable = ({ areasReclamo }) => {
  const [openModal, setOpenModal] = useState(false);
  const [step, setStep] = useState(STEP_RESPONSABLES_LISTA);

  const [usuarios, setUsuarios] = useState([]);
  const [datosOriginal, setDatosOriginal] = useState([]);
  const [datosOriginalSinValidar, setDatosOriginalSinValidar] = useState([]);
  const [usuariosSelected, setUsuariosSelected] = useState([]);
  const [agregarLoading, setAgregarLoading] = useState(false);
  const [eliminarLoading, setEliminarLoading] = useState(false);

  const [filters, setFilters] = useState({
    txtFiltro: '',
    area: 0
  });

  const onChangeFilters = (name, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value
    }));
  };

  const handleOpenModal = () => {
    setOpenModal(true);
    setStep(STEP_RESPONSABLES_LISTA);
  };

  const onCancel = () => {
    if (step === STEP_NUEVOS_RESPONSABLES) {
      setStep(STEP_RESPONSABLES_LISTA);
      setUsuariosSelected([]);
    } else {
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const onClickAddResponsable = () => {
    setStep(STEP_NUEVOS_RESPONSABLES);
  };

  const addNewResponsables = async () => {
    try {
      setAgregarLoading(true);
      const res = await agregarUsuarios({
        variables: {
          input: usuariosSelected.map((idUsuario) => ({ idArea: filters?.area, idUsuario: idUsuario }))
        }
      });

      if (!res?.data?.agregarUsuariosResponsable?.length) {
        throw new Error('Ocurrió un error, no se pudo asignar a los usuarios como responsables.');
      }

      toast.success('Usuarios asignados como responsables.');
      onCancel();
    } catch (error) {
      toast.error(error?.message || error);
    } finally {
      setAgregarLoading(false);
      getUsuarios({ variables: { idAreaReclamo: filters?.area } });
      getUsuariosSinValidar({ variables: { idArea: filters?.area } });
    }
  };

  const onDeleteUsuario = async (idUsuario) => {
    try {
      setEliminarLoading(true);
      const res = await eliminarUsuarioResponsable({
        variables: {
          idArea: filters?.area, idUsuario: idUsuario
        }
      });

      if (!res?.data?.eliminarUsuarioDeArea) {
        throw new Error('Ocurrió un error, no se pudo desasignar al usuario responsable del área seleccionada.');
      }

      toast.success('Usuario responsable desasignado del área seleccionada.');
    } catch (error) {
      toast.error(error?.message || error);
    } finally {
      setEliminarLoading(false);
      getUsuarios({ variables: { idAreaReclamo: filters?.area } });
      getUsuariosSinValidar({ variables: { idArea: filters?.area } });
    }
  };

  const handleTextClean = () => {
    onChangeFilters('txtFiltro', '');
  };

  const handleChangeFilters = (e) => {
    const { value, name } = e.target;
    onChangeFilters(name, value);
  };

  const onSelectUsuario = (idsUsuario) => {
    setUsuariosSelected(idsUsuario);
  };

  const [getUsuarios, { loading: usuariosLoading, data: usuariosCompletoData }] = useLazyQuery(GET_ALL_USUARIOS_RESPONSABLES_COMPLETO, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!usuariosCompletoData) return;

    const parsedUsuarios = adaptUsuariosResponsablesCompletoArray(usuariosCompletoData, onDeleteUsuario);
    setDatosOriginal(parsedUsuarios);
    if (step === STEP_RESPONSABLES_LISTA) {
      setUsuarios(parsedUsuarios);
    }
  }, [usuariosCompletoData]);

  const [getUsuariosSinValidar, { loading: usuariosSinValidarLoading, data: usuariosSinValidarData }] = useLazyQuery(GET_USUARIOS_RESPONSABLES_SIN_VALIDAR, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!usuariosSinValidarData) return;

    const parsedUsuarios = adaptUsuariosResponsablesCompletoArray(usuariosSinValidarData);
    setDatosOriginalSinValidar(parsedUsuarios);
  }, [usuariosSinValidarData]);

  const columns = useMemo(() => {
    const filteredColumns = step !== STEP_RESPONSABLES_LISTA ?
      initialColumns.filter((x) => x.field !== 'action') :
      initialColumns;
    return generateColumnsDefaultConfiguration(filteredColumns, true);
  }, [step]);

  const handleTextFilter = useCallback(() => {
    const inputBuscar = filters?.txtFiltro?.toUpperCase();
    const usuariosOriginales = step === STEP_RESPONSABLES_LISTA ? datosOriginal : datosOriginalSinValidar;

    if (inputBuscar?.length) {
      setUsuarios(usuariosOriginales?.filter((datoOriginal) => {
        const id = datoOriginal.id != '' ? datoOriginal.id.toString().toUpperCase() : '';
        const nombre = datoOriginal.nombre.toUpperCase();
        const apellido = datoOriginal.apellido.toUpperCase();
        const usuario = datoOriginal.usuario.toUpperCase();
        const email = datoOriginal.email.toUpperCase();

        return (
          id.includes(inputBuscar) ||
          nombre.includes(inputBuscar) ||
          apellido.includes(inputBuscar) ||
          usuario.includes(inputBuscar) ||
          email.includes(inputBuscar));

      }));
    } else {
      setUsuarios(usuariosOriginales);
    }
  }, [filters?.txtFiltro, step, datosOriginal, datosOriginalSinValidar]);

  const [agregarUsuarios] = useMutation(AGREGAR_USUARIOS_RESPONSABLE);
  const [eliminarUsuarioResponsable] = useMutation(ELIMINAR_USUARIO_RESPONSABLE);

  useEffect(() => {
    if (!filters?.area) return;
    getUsuarios({ variables: { idAreaReclamo: filters?.area } });
    getUsuariosSinValidar({ variables: { idArea: filters?.area } });
  }, [filters?.area]);

  useEffect(() => {
    setUsuarios(step === STEP_RESPONSABLES_LISTA ? datosOriginal : datosOriginalSinValidar);
  }, [step]);

  useEffect(() => {
    handleTextFilter();
  }, [filters?.txtFiltro, datosOriginalSinValidar, datosOriginal, step]);

  return (
    <div>
      <CustomButton
        className='button-header'
        label='Editar Responsables'
        onClick={handleOpenModal}
        variant='outlined' />

      <CustomDialog
        title={`${step === STEP_RESPONSABLES_LISTA ? 'Editar' : 'Agregar'} responsables`}
        maxWidth={'md'}
        fullWidth
        open={openModal}
        acceptText='Agregar'
        onAccept={step === STEP_NUEVOS_RESPONSABLES ? addNewResponsables : null}
        acceptDisabled={agregarLoading || !(usuariosSelected.length > 0)}
        acceptLoading={agregarLoading || eliminarLoading}
        cancelText={step === STEP_RESPONSABLES_LISTA ? 'Cerrar' : 'Volver'}
        onCancel={onCancel}
        onBackdropClick={handleCloseModal}>

        <div className='dialog-content'>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label='Buscar usuarios'
                name='txtFiltro'
                size='small'
                type='text'
                fullWidth
                variant='outlined'
                value={filters.txtFiltro}
                onChange={handleChangeFilters}
                disabled={agregarLoading || eliminarLoading || usuariosLoading || usuariosSinValidarLoading}
                onKeyUp={handleTextFilter}
                sx={{ backgroundColor: 'white' }}
                InputProps={{
                  endAdornment:
                    <InputAdornment position='end'>
                      <SearchIcon sx={{ cursor: 'pointer' }} />
                      <ClearIcon onClick={handleTextClean} sx={{ cursor: 'pointer' }} />
                    </InputAdornment>

                }} />

            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size='small'>
                <InputLabel id='select-area'>Área</InputLabel>
                <Select
                  labelId='select-area'
                  options={areasReclamo}
                  label='Área'
                  onChange={handleChangeFilters}
                  value={filters?.area || undefined}
                  disabled={agregarLoading || eliminarLoading || usuariosLoading || usuariosSinValidarLoading}
                  name='area'>

                  {areasReclamo && areasReclamo.map((area) => {
                    return <MenuItem key={area.id} value={area.id}> {area.displayName} </MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Grid>
            {step === STEP_RESPONSABLES_LISTA && <Grid display='flex' justifyContent='flex-end' size={{ xs: 12, md: 6 }}>
              <CustomButton
                variant='outlined'
                label='Agregar responsables'
                onClick={onClickAddResponsable} />

            </Grid>}
            <Grid size={{ xs: 12 }}>
              <DataGridGenerico
                rows={usuarios}
                columns={columns}
                loading={usuariosLoading || usuariosSinValidarLoading}
                checkboxSelection={step === STEP_NUEVOS_RESPONSABLES}
                onRowSelectionModelChange={onSelectUsuario}
                rowSelectionModel={usuariosSelected}
                keepNonExistentRowsSelected
                toolbar={false} />

            </Grid>
          </Grid>
        </div>
      </CustomDialog>
    </div>);

};

const initialColumns = [
  {
    flex: 10,
    field: 'action',
    headerName: '',
    renderCell: ({ row }) => {
      return (
        <Tooltip title='Eliminar' onClick={() => row?.onDelete(row?.id)}>
          <DeleteOutlineOutlinedIcon className='edit-icon' />
        </Tooltip>);

    }
  },
  {
    flex: 20,
    field: 'id',
    headerName: 'ID'
  },
  {
    flex: 40,
    field: 'usuario',
    headerName: 'Usuario'
  },
  {
    flex: 40,
    field: 'apellido',
    headerName: 'Apellido'
  },
  {
    flex: 40,
    field: 'nombre',
    headerName: 'Nombre'
  },
  {
    flex: 80,
    field: 'email',
    headerName: 'Email'
  }];


export default EditarResponsable;